import { isDefined } from '@sgme/fp';
import { batchWorkspaceApiConnector } from 'apiConnector/batch-workspace';
import { lastQuoteApi } from 'apiConnector/quote/lastquote';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { concat, EMPTY, merge, of, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, timeoutWith, withLatestFrom } from 'rxjs/operators';
import type { FunkyAction } from 'shared/middlewares/epic/epicMiddleware.factory';
import { batchPreviousQuoteLoaded } from 'state/batchForm/batchForm.action';
import { batchReloadFailedThunk, defaultBatchFormReceivedThunk } from 'state/batchForm/batchForm.thunks';
import { isUserPreferencesPrivateMode } from 'state/selectors';
import type { EpicWithThunk } from 'state/state.common';
import { HttpTimeoutError, type HttpClientError } from 'utils/http/httpClient.model';

export function reloadBatchFormEpic(timeoutInMillis: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (actions$, state$, { httpClient }) =>
    actions$.pipe(
      ofType('BATCH_FORM_RELOAD_REQUESTED'),
      withLatestFrom(state$),
      mergeMap(([action, state]) => {
        const {
          tabId,
          temporaryFormId,
          identifier,
          idType,
          tradeDate,
          productSubType,
          productType,
          reloadCorrelation,
        } = action;
        const isPrivateMode = isUserPreferencesPrivateMode(state);
        const occurrenceReloadReset$ = reloadCorrelation
          ? actions$.pipe(
              ofType('SCHEDULED_RUN_OCCURRENCE_RELOAD_RESET'),
              filter(
                resetAction =>
                  resetAction.id === reloadCorrelation.scheduledRunId &&
                  (resetAction.operationId === undefined || resetAction.operationId === reloadCorrelation.operationId),
              ),
            )
          : EMPTY;
        const scheduledRunChanged$ = reloadCorrelation
          ? actions$.pipe(
              ofType('SCHEDULED_RUN_SELECTED_RUN'),
              filter(selectedRunAction => selectedRunAction.id === reloadCorrelation.scheduledRunId),
            )
          : EMPTY;
        const cancellation$ = merge(tabRemoved(actions$, tabId, state$), occurrenceReloadReset$, scheduledRunChanged$);

        return batchWorkspaceApiConnector(httpClient)
          .reloadForm({
            id: identifier,
            idType,
            tradeDate,
            productSubType,
            isPrivateMode,
            productType,
          })
          .pipe(
            timeoutWith(
              timeoutInMillis,
              throwError(() => new HttpTimeoutError(timeoutInMillis)),
              appScheduler,
            ),
            mergeMap(reloadedForm => {
              const {
                fieldsMetadata,
                productView,
                values,
                version,
                negotiationId,
                actions,
                quoteQuery,
                errors,
                mustForceAdvancedMode,
                allowedRequests,
                quoteId,
              } = reloadedForm;

              const defaultBatchFormReceivedThunkAction = defaultBatchFormReceivedThunk(
                tabId,
                temporaryFormId,
                negotiationId,
                productView,
                actions,
                values,
                fieldsMetadata,
                quoteQuery,
                version,
                mustForceAdvancedMode ?? false,
                allowedRequests,
                errors,
                reloadCorrelation,
              );

              const previousQuoteLoadedAction$ = isDefined(quoteId)
                ? lastQuoteApi(httpClient)
                    .getLastQuote(quoteId)
                    .pipe(
                      map(lastQuote => batchPreviousQuoteLoaded(negotiationId, quoteId, lastQuote.quote)),
                      catchError(() => EMPTY),
                    )
                : EMPTY;

              return concat(of(defaultBatchFormReceivedThunkAction), previousQuoteLoadedAction$);
            }),
            catchError((error: HttpClientError) => {
              return of<FunkyAction>(
                batchReloadFailedThunk(tabId, temporaryFormId, identifier, error, reloadCorrelation),
              );
            }),
            takeUntil(cancellation$),
          );
      }),
    );
}
